<template>
<div class="pa-4">
  <v-data-table
    :headers="headers"
    :items="items"
    :loading="loading"
    :single-expand="true"
    item-key="id"
    :items-per-page="10"
    sort-by="date_emited"
    show-expand
    class="modern-table border rounded overflow-hidden elevation-0"
  >
    <template v-slot:no-data>
      <v-alert type="info" text dense icon="fas fa-info-circle" class="ma-4">
        No existen facturas registradas para esta orden.
      </v-alert>
    </template>

    <template #item.document_id="{ item }">
      <div class="d-flex flex-column">
        <span class="text-h6 font-weight-bold">#{{item.document_id}}</span>
        <div class="d-flex align-center">
          <v-chip
            v-if="item.tipo == 60 || item.tipo == 61" 
            x-small
            color="error"
            label
            class="mr-2"
          >
            N. Crédito
          </v-chip>
          <v-chip
            v-else-if="item.tipo == 52"
            x-small
            color="info"
            label
            class="mr-2"
          >
            Guía
          </v-chip>
          <v-chip
            v-else-if="item.source === 'simplefactura' || item.source === 'facturacioncl'"
            x-small
            :color="item.source === 'facturacioncl' ? 'teal' : 'primary'"
            label
            class="mr-2"
          >
            {{ item.tipo_display || 'Factura' }}
          </v-chip>
          <span :class="`${getStatusColor(item.estatus)}--text`" class="text-caption font-weight-bold text-uppercase">
            {{getStatusText(item.estatus)}}
          </span>
        </div>
      </div>
    </template>

    <template #item.date_emited="{ item }">
      <div class="body-2">{{item.date_emited | moment('DD/MM/YYYY')}}</div>
    </template>

    <template #item.payment_date="{ item }">
      <div v-if="item.payment_date" class="body-2">{{ item.payment_date | moment('DD/MM/YYYY') }}</div>
      <div v-else class="body-2 grey--text">—</div>
    </template>

    <template #item.rznSocRecep="{ item }">
      <div class="d-flex flex-column">
        <span class="body-2 font-weight-medium">{{item.rznSocRecep}}</span>
        <span class="caption grey--text">{{item.rutRecep}}</span>
      </div>
    </template>

    <template #item.get_total="{ item }">
      <span class="font-weight-bold">{{item.get_total | moneda}}</span>
    </template>

    <template #item.payed="{ item }">
      <div class="d-flex flex-column align-end">
        <span :class="item.get_total - item.get_payed > 0 ? 'error--text' : 'success--text'" class="font-weight-bold">
          {{item.get_payed | moneda}}
        </span>
        <span v-if="item.get_total - item.get_payed > 0" class="caption grey--text">
          Pend: {{(item.get_total - item.get_payed) | moneda}}
        </span>
      </div>
    </template>

    <template v-slot:expanded-item="{ headers, item }">
      <td :colspan="headers.length" class="expanded-row-bg pa-6">
        <v-card outlined class="elevation-2 overflow-hidden">
          <invoice-detail :invoice="item" :inSet="true"/>
        </v-card>
      </td>
    </template> 
  </v-data-table>
</div>
</template>

<script>
import { fetchDTE } from '@/api/salesNotes'
import { fetchDocuments, mapDocumentToInvoiceRow } from '@/api/simplefactura'
import { fetchDocuments as fetchFclDocuments, mapDocumentToInvoiceRow as mapFclDocumentToInvoiceRow } from '@/api/facturacioncl'
import InvoiceDetail from '@/components/sales/InvoiceDetail'
import notifications from '@/utils/notifications'

export default {
  name: 'Invoices',
  components: { InvoiceDetail },
  props: ['workorder'],
  data() {
    return {
      loading: false,
      items: [],
      headers: [
        { text: 'Documento', value: 'document_id', align: 'start', width: '150' },
        { text: 'Fecha', value: 'date_emited', align: 'start', width: '120' },
        { text: 'Vence', value: 'payment_date', align: 'start', width: '120' },
        { text: 'Cliente', value: 'rznSocRecep', align: 'start' },
        { text: 'Total', value: 'get_total', align: 'end', width: '120' },
        { text: 'Pagado', value: 'payed', align: 'end', width: '120' },
        { text: '', value: 'data-table-expand', align: 'end', width: '50' },
      ],
      estatuses: [
        { value:1, text:'Ingresada', color:'info' },
        { value:2, text:'Aprobada', color:'success' },
        { value:4, text:'Recepcionada', color:'grey' },
        { value:3, text:'Anulada', color:'error' },
      ],
    }
  },
  methods: {
    getInvoices() {
      this.loading = true
      const simpleFacturaEnabled = !!this.$store.getters.getCompany?.simplefactura_integration_enabled
      if (simpleFacturaEnabled) {
        const nested = this.workorder.simplefactura_documents
        if (Array.isArray(nested) && nested.length) {
          this.items = nested.map(doc => mapDocumentToInvoiceRow(doc, this.workorder.customer_name, this.workorder))
          this.loading = false
          return
        }
        fetchDocuments(`page=1&page_size=100&ordering=-created_at&workorder=${this.workorder.id}`)
          .then(data => {
            this.items = (data.results || []).map(doc => mapDocumentToInvoiceRow(doc, this.workorder.customer_name, this.workorder))
          })
          .catch(err => {
            notifications.showError(err)
          })
          .finally(() => {
            this.loading = false
          })
        return
      }
      const facturacionClEnabled = !!this.$store.getters.getCompany?.facturacioncl_integration_enabled
      if (facturacionClEnabled) {
        const nested = this.workorder.facturacioncl_documents
        if (Array.isArray(nested) && nested.length) {
          this.items = nested.map(doc => mapFclDocumentToInvoiceRow(doc, this.workorder.customer_name, this.workorder))
          this.loading = false
          return
        }
        fetchFclDocuments(`page=1&page_size=100&ordering=-created_at&workorder=${this.workorder.id}`)
          .then(data => {
            this.items = (data.results || []).map(doc => mapFclDocumentToInvoiceRow(doc, this.workorder.customer_name, this.workorder))
          })
          .catch(err => {
            notifications.showError(err)
          })
          .finally(() => {
            this.loading = false
          })
        return
      }
      const query = `work_order=${this.workorder.id}`
      fetchDTE(query).then(data => {
        this.items = data.results.filter(i => i.estatus < 3)
      })
      .catch(err => {
        notifications.showError(err)
      })
      .finally(() => {
        this.loading = false
      })
    },
    getStatusColor(val) {
      const status = this.estatuses.find(e => e.value == val)
      return status ? status.color : 'grey'
    },
    getStatusText(val) {
      const status = this.estatuses.find(e => e.value == val)
      return status ? status.text : 'Desconocido'
    }
  },
  async created() {
    this.getInvoices()
  }
}
</script>

<style scoped>
.modern-table.border {
  border: 1px solid rgba(0,0,0,0.08) !important;
}
.expanded-row-bg {
  background-color: #f1f3f4 !important;
}
</style>